import React, { useState, useEffect, useMemo } from "react";
import { sleep } from "../shared/util";
import { Container } from "../shared";
import { getAudioForPhonemes } from "../shared/phonemeToAudio";
import useConversation from "../shared/useConversation";
import "./UpAndDown.scss";
import icon from "./upanddown-icon.png";
import { listen } from "Services/Listen";
import { getUserData } from "Services/API";
import Slide from "./Slide";
import useCancellablePromise from "../shared/hooks/useCancellablePromise";
import { COPY_STRINGS } from "Constants/translatedCopy";

const EVENTS = {
  start: "start",
  retry: "retry",
  end: "end",
};

const SPANISH_DEMO = {
  DEMO_WORD: "gato",
  DEMO_PHON: "g'a/to",
  DEMO_GRAPHEMES: ["g", "a", "t", "o"],
}

const ENGLISH_DEMO_TUTOR = {
  DEMO_WORD: "can",
  DEMO_PHON: "kˈæn",
  DEMO_GRAPHEMES: ["c", "a", "n"],
}

const STAGES = {
  "DEMO": 0,
  "LISTEN_PHONEMES": 1,
  "LISTEN_WORD": 2,
}

/**
 * 
 * @param {Boolean} tutorMode 
 * @param {String} locale 
 * @returns 
 */
function getDemoObject(locale){
  if(locale === 'es-mx'){
    return SPANISH_DEMO;
  } 
  return ENGLISH_DEMO_TUTOR;
}

export default function UpAndDownAssessment (props) {
  const { locale } = getUserData();

  const { example, word, paused, didAttempt, wordMetadata, speech, ftue, phrase, isInstruction, hasInstructions, currentWordIndex} = props;

  const { DEMO_WORD, DEMO_PHON, DEMO_GRAPHEMES } = useMemo(() => {
    if(example){
      return {
        DEMO_WORD: example.word,
        DEMO_PHON: example.PHON,
        DEMO_GRAPHEMES: example.GRAPHEMIC_BREAKDOWN.split("/"),
      }
    }
    return getDemoObject(locale);
  }, [example, isInstruction])

  const ttsStrings = useMemo(() => COPY_STRINGS.intervention_strings.up_and_down, []);
  const DEMO_FILES = useMemo(() => getAudioForPhonemes(DEMO_PHON, locale), [DEMO_PHON, locale]);
  const graphemes = useMemo(() => wordMetadata?.GRAPHEMIC_BREAKDOWN?.split("/"), [wordMetadata]);
  
  const [stage, setStage] = useState(ftue ? 0 : 1);
  const [graphemeIndex, setGraphemeIndex] = useState(-1);

  const { speak, speakSequence, speakSlowly, speakFile } = useConversation(speech, listen);
  const { cancellablePromise } = useCancellablePromise();

  // listen for stage changes
  useEffect(() => {
    onStageChange();
  }, [stage, isInstruction]);

  // listen for pauses
  useEffect(()=>{
    if(paused && !didAttempt && stage !== STAGES.DEMO){
      handlePause();
    }
  }, [paused])

  // listen for finished with phonemes, go to listen word stage when the last word is reached
  useEffect(() => {
    if (stage!== STAGES.DEMO && currentWordIndex === phrase?.split(' ').length - 1) {
      setStage(STAGES.LISTEN_WORD);
    }
  }, [currentWordIndex, phrase]);

  // listen for currentWordIndex changed, move the bear by setting the grapheme index
  useEffect(() => {
    if (currentWordIndex !== undefined) {
      setGraphemeIndex(currentWordIndex - 1);
    }
  }, [currentWordIndex]);

  /**
   * handle the pause event, it should ask for the sound or word.
   */
  async function handlePause() {
    if(!didAttempt){
      if(stage === STAGES.LISTEN_PHONEMES){
        await cancellablePromise(speakPromptSounds(word));
      } else if(stage === STAGES.LISTEN_WORD){
        await cancellablePromise(speak(ttsStrings.ask_blend_assessment));
      }
      props.dispatchEvent(EVENTS.retry);
    }else{
      props.dispatchEvent(EVENTS.end);
      console.log("force end of handling pauses, give control back to the reader manager.")
    }
  }

  /**
   * handle the demo stage, it should go through each phoneme and then the word.
   */
  async function handleDemo() {
    // the Amira demo phrase -> goes through each phoneme -> and then the word.
    props.speech.stopListening();
    if(getUserData().bilingualMode) {
      await cancellablePromise(speak(ttsStrings.intro_1_tutor));
      await cancellablePromise(speakSlowly({textToSpeak: DEMO_WORD, skipTranslation: true}));
    } else {
      await cancellablePromise(speak(`${ttsStrings.intro_1_tutor} "${DEMO_WORD}".`));
    }
    await cancellablePromise(sleep());
    const length = DEMO_FILES.length;
    for (let i = 0; i < length; i++) {
      await cancellablePromise(speakFile(DEMO_FILES[i]));
      setGraphemeIndex(i);
      await cancellablePromise(sleep());
    }
    if(getUserData().bilingualMode) {
      await cancellablePromise(speakSlowly({textToSpeak: DEMO_WORD, skipTranslation: true}));
    } else {
      await cancellablePromise(speak(`"${DEMO_WORD}".`));
    }
    setGraphemeIndex(length + 1);
    await cancellablePromise(sleep());
    setGraphemeIndex(-1);
    await cancellablePromise(speak(ttsStrings.ask_sounds_tutor_0));
    setStage(STAGES.LISTEN_PHONEMES);
  }

  /**
   * handle the listen phonemes stage, it should ask for the phonemes of the word.
   */
  async function handleStageListenPhonemes() {
    await cancellablePromise(speakAskSounds(word));
    // listen for the phonemes to move the bear up the stairs
    props.dispatchEvent(EVENTS.start);
  }

  /**
   * handle the listen word stage, it should ask for the word.
   */
  async function handleStageListenWord() {
    // do nothing, but we can prompt for the blended word here.
  }

  /**
   * handle the stage change, it should call the corresponding function for the stage.
   */
  async function onStageChange() {
    if (!props.isMountedRef.current) return;
    
    try {
      if(isInstruction){
        return; // do nothing if it's an instruction
      }
      switch (stage) {
        case STAGES.DEMO: {
          await handleDemo();
          break;
        }
        case STAGES.LISTEN_PHONEMES: {
          await handleStageListenPhonemes();
          break;
        }
        case STAGES.LISTEN_WORD: {
          await handleStageListenWord();
          break;
        }
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  /**
   * render the component.
   */
  return (
    <Container icon={icon}>
      {renderContent()}
    </Container>
  );

  function renderContent() {
    if (stage === 0) {
      return (
        <Slide
          steps={DEMO_GRAPHEMES.length}
          graphemeIndex={graphemeIndex}
          versionId={props.versionId}
          graphemes={DEMO_GRAPHEMES}
        />
      );
    }

    return (
      <Slide
        steps={graphemes.length}
        graphemeIndex={graphemeIndex}
        versionId={props.versionId}
        graphemes={graphemes}
        onClickCreature={async () => {
          console.log("do nothing for assessment mode")
        }}
      />
    );
  }

  /**
   * ask the student to say the sounds of the word.
   * @param {String} word 
   * @param {Boolean} tutorMode 
   */
  async function speakAskSounds(word) {
    const askSoundsString1 = ttsStrings.ask_sounds_tutor_1;
    const askSoundsString2 = ttsStrings.ask_sounds_tutor_2;
    const askSoundsString3 = ttsStrings.ask_sounds_tutor_3;

    if(getUserData().bilingualMode) {
      await speakSequence([
        askSoundsString1,
        {textToSpeak: word, skipTranslation: true},
        askSoundsString2,
        {textToSpeak: word, skipTranslation: true},
        askSoundsString3,
      ]);
    }
    else {
      await speak(`${askSoundsString1} "${word}". ${askSoundsString2} "${word}", ${askSoundsString3}`);
    }
  }

  /**
   * ask the student to retry the sounds.
   * @param {*} word 
   */
  async function speakPromptSounds(word) {
    const prompt = ttsStrings.prompt_sounds_assessment;
    if(getUserData().bilingualMode) {
      await speakSequence([
        prompt,
        {textToSpeak: word, skipTranslation: true},
      ]);
    }
    else {
      await speak(`${prompt} "${word}".`);
    }
  }
}
